// src/api/centralTestingModeApi.ts
// RTK Query API for Central Testing Mode endpoints (dev/staging only)
//
// IMPORTANT: Uses credentials: 'include' for session-bound requests.
// Types and normalization helpers from @levelup/central-auth.

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  TESTING_MODE_PATHS,
  normalizeTestingModeOptions,
  normalizeTestingModeStatus,
} from '@levelup/central-auth';
import type {
  TestingModeOptions,
  TestingModeStatus,
  ApplyOverrideParams,
} from '@levelup/central-auth';
import { CENTRAL_URL } from '../config/centralAuth';
import type { RootState } from '../store/store';

// ============================================================
// API Definition
// ============================================================

export const centralTestingModeApi = createApi({
  reducerPath: 'centralTestingModeApi',
  baseQuery: fetchBaseQuery({
    baseUrl: CENTRAL_URL,
    credentials: 'include', // Required: Central Testing Mode is session-bound
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).centralAuth.accessToken;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['TestingModeStatus'],
  endpoints: (builder) => ({
    // GET /api/internal/testing-mode/options
    getTestingModeOptions: builder.query<TestingModeOptions, void>({
      query: () => TESTING_MODE_PATHS.options,
      transformResponse: (raw: unknown) => normalizeTestingModeOptions(raw),
    }),

    // GET /api/internal/testing-mode/status
    getTestingModeStatus: builder.query<TestingModeStatus, void>({
      query: () => TESTING_MODE_PATHS.status,
      providesTags: ['TestingModeStatus'],
      transformResponse: (raw: unknown) => normalizeTestingModeStatus(raw),
    }),

    // POST /api/internal/testing-mode/override
    applyTestingModeOverride: builder.mutation<TestingModeStatus, ApplyOverrideParams>({
      query: (params) => ({
        url: TESTING_MODE_PATHS.override,
        method: 'POST',
        body: params,
      }),
      invalidatesTags: ['TestingModeStatus'],
    }),

    // DELETE /api/internal/testing-mode/override
    clearTestingModeOverride: builder.mutation<void, void>({
      query: () => ({
        url: TESTING_MODE_PATHS.override,
        method: 'DELETE',
      }),
      invalidatesTags: ['TestingModeStatus'],
    }),
  }),
});

export const {
  useGetTestingModeOptionsQuery,
  useGetTestingModeStatusQuery,
  useApplyTestingModeOverrideMutation,
  useClearTestingModeOverrideMutation,
} = centralTestingModeApi;
